<script lang="ts">
  import Icon from './Icon.svelte';
  import { marked } from 'marked';
  import TurndownService from 'turndown';
  import { renderMarkdown } from '../lib/markdown';

  // WYSIWYG editor: contentEditable div shows formatted text directly (no
  // visible ** markers), `value` stays the markdown source so the rest of
  // the app keeps storing / rendering plain markdown unchanged.
  //
  // Flow:
  //   - mount: marked(value) → innerHTML
  //   - user types: input event → turndown(innerHTML) → value
  //   - external value change: $effect re-renders innerHTML, guarded by a
  //     flag so our own input-driven updates don't reflow the cursor
  interface Props {
    value: string;
    maxlength?: number;
    placeholder?: string;
    /** Fired on every input — lets the host treat this as a controlled
     *  component without needing `bind:value`. Useful when value lives inside
     *  a larger structure (e.g. a block in BlockEditor). */
    onChange?: (next: string) => void;
  }
  let {
    value = $bindable(),
    maxlength = 4000,
    placeholder = 'Почніть писати…',
    onChange,
  }: Props = $props();

  // Match the marked options used in lib/markdown.ts so what the editor
  // shows is what the user will see on the read view.
  marked.setOptions({ breaks: true, gfm: true });
  const turndown = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    emDelimiter: '_',
    strongDelimiter: '**',
    linkStyle: 'inlined',
  });
  // Markdown doesn't have a portable underline syntax, so we keep <u> as raw
  // HTML in the source — marked passes it through unchanged on render.
  turndown.addRule('underline', {
    filter: ['u'],
    replacement: (content) => `<u>${content}</u>`,
  });
  // Strikethrough is GFM-native: ~~text~~ renders as <del> via marked.
  turndown.addRule('strikethrough', {
    // `strike` is a legacy tag some browsers' execCommand still emits, but it
    // is absent from lib.dom's tag map — cast just that entry.
    filter: ['s', 'del', 'strike' as keyof HTMLElementTagNameMap],
    replacement: (content) => `~~${content}~~`,
  });

  let editor: HTMLDivElement | undefined = $state(undefined);
  // True when the most recent `value` change came from this editor's own
  // input event — used to skip the md→html sync that would otherwise wipe
  // the cursor mid-typing.
  let selfUpdated = false;

  $effect(() => {
    void value;
    if (!editor) return;
    if (selfUpdated) {
      selfUpdated = false;
      return;
    }
    const html = renderMarkdown(value);
    if (editor.innerHTML !== html) editor.innerHTML = html;
  });

  function onInput(): void {
    if (!editor) return;
    // Empty contentEditable returns "<br>" — normalise to ''.
    const html = editor.innerHTML === '<br>' ? '' : editor.innerHTML;
    const md = turndown.turndown(html).trim();
    if (md.length > maxlength) {
      // Hard cap; we don't try to be clever about restoring caret position.
      editor.innerHTML = renderMarkdown(value);
      return;
    }
    selfUpdated = true;
    value = md;
    onChange?.(md);
  }

  function exec(cmd: string, arg?: string): void {
    if (!editor) return;
    editor.focus();
    document.execCommand(cmd, false, arg);
    onInput();
  }

  function insertLink(): void {
    const url = prompt('Посилання:', 'https://');
    if (!url) return;
    exec('createLink', url);
  }

  function clearFormat(): void {
    exec('removeFormat');
  }

  /** formatBlock('blockquote') doesn't toggle off on second invocation —
   *  the browser just keeps the existing wrapper. Walk up from the caret;
   *  if we're inside a <blockquote>, swap it for a <p>; otherwise wrap. */
  function toggleBlockquote(): void {
    if (!editor) return;
    editor.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      exec('formatBlock', 'blockquote');
      return;
    }
    let node: Node | null = sel.anchorNode;
    while (node && node !== editor) {
      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'BLOCKQUOTE') {
        exec('formatBlock', 'p');
        return;
      }
      node = node.parentNode;
    }
    exec('formatBlock', 'blockquote');
  }
</script>

<div class="md-editor">
  <div class="md-toolbar">
    <button type="button" class="md-btn md-bold" onmousedown={(e) => e.preventDefault()} onclick={() => exec('bold')} title="Жирний (⌘B)">B</button>
    <button type="button" class="md-btn md-italic" onmousedown={(e) => e.preventDefault()} onclick={() => exec('italic')} title="Курсив (⌘I)">I</button>
    <button type="button" class="md-btn md-underline" onmousedown={(e) => e.preventDefault()} onclick={() => exec('underline')} title="Підкреслено (⌘U)">U</button>
    <button type="button" class="md-btn md-strike" onmousedown={(e) => e.preventDefault()} onclick={() => exec('strikeThrough')} title="Закреслено">S</button>
    <button type="button" class="md-btn" onmousedown={(e) => e.preventDefault()} onclick={() => exec('insertUnorderedList')} title="Список">•</button>
    <button type="button" class="md-btn" onmousedown={(e) => e.preventDefault()} onclick={() => exec('insertOrderedList')} title="Нумерований список">1.</button>
    <button type="button" class="md-btn" onmousedown={(e) => e.preventDefault()} onclick={toggleBlockquote} title="Цитата">❝</button>
    <button type="button" class="md-btn" onmousedown={(e) => e.preventDefault()} onclick={insertLink} title="Посилання"><Icon name="link" size="xs" /></button>
    <button type="button" class="md-btn" onmousedown={(e) => e.preventDefault()} onclick={clearFormat} title="Очистити форматування">⌫</button>
  </div>
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="md-field md-content"
    contenteditable="true"
    role="textbox"
    aria-multiline="true"
    aria-label={placeholder}
    data-placeholder={placeholder}
    bind:this={editor}
    oninput={onInput}
  ></div>
</div>

<style>
  .md-editor {
    display: flex;
    flex-direction: column;
  }
  .md-toolbar {
    display: flex;
    gap: 4px;
    padding: 6px;
    background: var(--bg2);
    border: 1px solid var(--border2);
    border-bottom: none;
    border-radius: 10px 10px 0 0;
    flex-wrap: wrap;
  }
  .md-btn {
    min-width: 28px;
    height: 28px;
    padding: 0 8px;
    border: 1px solid transparent;
    background: transparent;
    color: var(--text);
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .md-btn:hover {
    background: var(--bg);
    border-color: var(--border2);
  }
  .md-btn.md-bold { font-weight: 700; }
  .md-btn.md-italic { font-style: italic; }
  .md-btn.md-underline { text-decoration: underline; }
  .md-btn.md-strike { text-decoration: line-through; }
  .md-field {
    width: 100%;
    border: 1px solid var(--border2);
    border-radius: 0 0 10px 10px;
    padding: 11px 13px;
    font-size: 13px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
    min-height: 80px;
    line-height: 1.5;
  }
  .md-field:focus { border-color: var(--outline); }
  .md-field:empty::before {
    content: attr(data-placeholder);
    color: var(--text-muted);
    pointer-events: none;
  }
</style>
